/**
 * Module de reconnaissance vocale via Web Speech API.
 * Parse les commandes naturelles du type :
 *   "3 yaourts nature" → { quantity: 3, name: "yaourts nature" }
 *   "pain" → { quantity: 1, name: "pain" }
 */

const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

/**
 * Vérifie si la reconnaissance vocale est disponible.
 * @returns {boolean}
 */
export function isSpeechAvailable() {
  return !!SpeechRecognition;
}

/**
 * Parse un texte vocal en nom d'article et quantité.
 * Gère les formats :
 *   "3 yaourts nature"
 *   "yaourts nature fois 3"
 *   "yaourts nature x 3"
 *   "une baguette"
 *   "deux paquets de pâtes"
 *   "pain"
 *
 * @param {string} text
 * @returns {{ name: string, quantity: number }}
 */
export function parseVoiceInput(text) {
  const trimmed = text.trim().toLowerCase();
  if (!trimmed) return { name: '', quantity: 1 };

  // Table des nombres en lettres
  const numberWords = {
    un: 1, une: 1, deux: 2, trois: 3, quatre: 4, cinq: 5,
    six: 6, sept: 7, huit: 8, neuf: 9, dix: 10,
    onze: 11, douze: 12, quinze: 15, vingt: 20,
  };

  // Format "X article" (chiffre au début)
  const leadingNumber = trimmed.match(/^(\d+)\s+(.+)$/);
  if (leadingNumber) {
    return { name: capitalize(leadingNumber[2]), quantity: parseInt(leadingNumber[1], 10) };
  }

  // Format "mot-nombre article" (ex: "trois baguettes")
  const words = trimmed.split(/\s+/);
  if (words.length >= 2 && numberWords[words[0]]) {
    return { name: capitalize(words.slice(1).join(' ')), quantity: numberWords[words[0]] };
  }

  // Format "article fois X" ou "article x X"
  const trailingMult = trimmed.match(/^(.+?)(?:\s+(?:fois|x|×)\s*)(\d+)$/);
  if (trailingMult) {
    return { name: capitalize(trailingMult[1]), quantity: parseInt(trailingMult[2], 10) };
  }

  return { name: capitalize(trimmed), quantity: 1 };
}

/**
 * Met la première lettre en majuscule.
 * @param {string} str
 * @returns {string}
 */
function capitalize(str) {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Lance une écoute vocale et retourne le texte reconnu.
 * @param {{ lang?: string, onStart?: () => void, onEnd?: () => void }} options
 * @returns {Promise<string>}
 */
export function listenVoice({ lang = 'fr-FR', onStart, onEnd } = {}) {
  return new Promise((resolve, reject) => {
    if (!SpeechRecognition) {
      reject(new Error('Reconnaissance vocale non supportée'));
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = lang;
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    recognition.continuous = false;

    recognition.onstart = () => onStart?.();

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      resolve(transcript);
    };

    recognition.onerror = (event) => {
      if (event.error === 'no-speech') {
        resolve('');
      } else {
        reject(new Error(event.error));
      }
    };

    recognition.onend = () => onEnd?.();

    recognition.start();
  });
}
